import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { 
  Box, 
  Typography, 
  FormControl, 
  Select, 
  Menu,
  MenuItem, 
  InputLabel,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip as MuiTooltip
} from "@mui/material";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import dayjs from "dayjs";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadarIcon from "@mui/icons-material/Radar";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import DateRangeIcon from "@mui/icons-material/DateRange";
import DateRangePicker from "./common/DateRangePicker";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import AppBarCommon from "./AppBarCommon";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./HomePage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const HomePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().subtract(13, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [deviceId, setDeviceId] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [considerOverrides, setConsiderOverrides] = useState(true);
  const [operatorStatus, setOperatorStatus] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateRangeAnchor, setDateRangeAnchor] = useState(null);
  const axiosPrivate = useAxiosPrivate();

  const formatDateRangeLabel = (start, end) => {
    if (!start && !end) return "Select date range";
    if (start && end && start.isValid() && end.isValid()) {
      if (start.isSame(end, "day")) {
        return start.format("MMM DD, YYYY");
      }
      if (start.year() === end.year()) {
        return `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`;
      }
      return `${start.format("MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
    }
    if (start && start.isValid()) return `From ${start.format("MMM DD, YYYY")}`;
    if (end && end.isValid()) return `Until ${end.format("MMM DD, YYYY")}`;
    return "Select date range";
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/defect-inspection/v1/dashboard?start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
      if (deviceId) url += `&device_id=${deviceId}`;
      if (selectedDay) url += `&selected_day=${selectedDay}`;
      url += `&consider_overrides=${considerOverrides}`;
      if (operatorStatus) url += `&operator_status=${operatorStatus}`;

      const response = await axiosPrivate.get(url);
      setData(response.data);
      if (!selectedDay && response.data.hourly_distribution?.date) {
          setSelectedDay(response.data.hourly_distribution.date);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, startDate, endDate, deviceId, selectedDay, considerOverrides, operatorStatus]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handlePrint = async () => {
    const input = document.querySelector('.dashboard-container');

    try {
      const canvas = await html2canvas(input, { 
        scale: 2, 
        useCORS: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          const clonedContainer = clonedDoc.querySelector('.dashboard-container');
          
          // Hide navigational elements in clone
          const actions = clonedDoc.querySelector('.header-actions');
          const appBar = clonedDoc.querySelector('.MuiAppBar-root');
          const nav = clonedDoc.querySelector('nav');
          
          if (actions) actions.style.display = 'none';
          if (appBar) appBar.style.display = 'none';
          if (nav) nav.style.display = 'none';
          
          clonedContainer.style.backgroundColor = '#ffffff';
          clonedContainer.style.paddingTop = '0px';

          // Create Report Header in clone
          const header = clonedDoc.createElement('div');
          header.style.display = 'flex';
          header.style.justifyContent = 'space-between';
          header.style.alignItems = 'center';
          header.style.marginBottom = '20px';
          header.style.padding = '15px 20px';
          header.style.backgroundColor = '#fff';
          header.style.borderRadius = '8px';
          header.style.border = '1px solid #e0e0e0';
          header.innerHTML = `
            <div style="display: flex; align-items: center; gap: 15px;">
              <div style="width: 30px; height: 30px; border-radius: 50%; background: #294A50; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-family: sans-serif; font-size: 14px;">BP</div>
              <div>
                <h2 style="margin: 0; font-size: 18px; color: #294A50; font-family: 'Work Sans', sans-serif; font-weight: 700;">BrakePress Defect Inspection — Dashboard</h2>
                <p style="margin: 0; font-size: 11px; color: #666; margin-top: 4px; font-family: 'Work Sans', sans-serif;">
                  ${startDate.format('MMM D')} — ${endDate.format('MMM D, YYYY')} • ${data?.date_range.days} days • ${considerOverrides ? "Final Status (Operator Overrides Included)" : "AI Model Predictions Only"}${deviceId ? ` • Device: ${data?.devices.find(d => d.device_id === deviceId)?.device_name || deviceId}` : ""}${operatorStatus ? ` • Overrides: ${operatorStatus === 'all' ? 'Overridden Only' : operatorStatus === 'none' ? 'Non-Overridden Only' : operatorStatus}` : ""} • Offline Report
                </p>
              </div>
            </div>
            <div>
              <p style="margin: 0; font-size: 10px; color: #666; border: 1px solid #eee; padding: 6px 12px; border-radius: 4px; background-color: #fcfcfc; font-family: 'Work Sans', sans-serif;">
                Generated ${dayjs().format('M/D/YYYY, h:mm:ss A')}
              </p>
            </div>
          `;

          // Create Report Footer in clone
          const footer = clonedDoc.createElement('div');
          footer.style.textAlign = 'center';
          footer.style.marginTop = '40px';
          footer.style.padding = '20px 0';
          footer.style.borderTop = '1px dashed #e0e0e0';
          footer.style.fontSize = '10px';
          footer.style.color = '#888';
          footer.style.fontFamily = "'Work Sans', sans-serif";
          footer.innerHTML = 'BrakePress Defect Inspection • Offline Report • All data embedded • No external dependencies';

          // Add to cloned container
          clonedContainer.insertBefore(header, clonedContainer.firstChild);
          clonedContainer.appendChild(footer);
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      const pxToMm = 0.264583;
      const pdfWidth = canvas.width * pxToMm;
      const pdfHeight = canvas.height * pxToMm;
      
      // Create a single-page PDF that exactly matches the canvas dimensions
      const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight]);
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      pdf.save(`dashboard_report_${dayjs().format('YYYY-MM-DD')}.pdf`);
    } catch (error) {
      console.error("PDF generation error:", error);
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#eaedeb' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: "#294A50" }} />
      </Box>
    );
  }

  // Chart Data Configurations
  const dailyPassRejectData = {
    labels: data?.daily_summary.map(d => dayjs(d.date).format('MMM D')) || [],
    datasets: [
      {
        label: 'Pass',
        data: data?.daily_summary.map(d => d.passed) || [],
        backgroundColor: '#13A13E',
      },
      {
        label: 'Reject',
        data: data?.daily_summary.map(d => d.rejected) || [],
        backgroundColor: '#E25984',
      }
    ]
  };

  const overallPassRejectData = {
    labels: ['Pass', 'Reject'],
    datasets: [
      {
        data: [data?.kpi.passed, data?.kpi.rejected],
        backgroundColor: ['#13A13E', '#E25984'],
        borderWidth: 0,
        cutout: '75%',
      }
    ]
  };

  const passRateTrendData = {
    labels: data?.daily_summary.map(d => dayjs(d.date).format('MMM D')) || [],
    datasets: [
      {
        label: 'Pass Rate %',
        data: data?.daily_summary.map(d => d.pass_rate) || [],
        borderColor: '#239BB9',
        backgroundColor: 'rgba(35, 155, 185, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#239BB9',
      }
    ]
  };

  const hourlyDistData = {
    labels: data?.hourly_distribution.data.map(d => d.display_hour) || [],
    datasets: [
      {
        label: 'Pass',
        data: data?.hourly_distribution.data.map(d => d.passed) || [],
        backgroundColor: '#13A13E',
      },
      {
        label: 'Reject',
        data: data?.hourly_distribution.data.map(d => d.rejected) || [],
        backgroundColor: '#E25984',
      }
    ]
  };

  const profileVolumeData = {
    labels: data?.profile_summary.map(p => p.profile) || [],
    datasets: [
      {
        label: 'Pass',
        data: data?.profile_summary.map(p => p.passed) || [],
        backgroundColor: '#13A13E',
      },
      {
        label: 'Reject',
        data: data?.profile_summary.map(p => p.rejected) || [],
        backgroundColor: '#E25984',
      }
    ]
  };

  const profileTrendData = {
    labels: data?.daily_summary.map(d => dayjs(d.date).format('MMM D')) || [],
    datasets: data?.profile_daily_trend.map((p, idx) => ({
      label: p.profile,
      data: data.daily_summary.map(ds => {
        const found = p.data.find(pd => pd.date === ds.date);
        return found ? found.total : 0;
      }),
      borderColor: `hsl(${(idx * 137.5) % 360}, 70%, 50%)`,
      fill: false,
      tension: 0.3,
    })) || []
  };

  return (
    <div className="dashboard-container" style={{ paddingTop: '100px' }}>
      <AppBarCommon name="Home" />
      
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-title-section">
          <span className="dashboard-subtitle" style={{ fontSize: '14px', fontWeight: 600, color: '#294A50', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span>{startDate.format('MMM D')} — {endDate.format('MMM D, YYYY')} • {data?.date_range.days} days</span>
          </span>
        </div>

        <div className="header-actions">
           {/* Date Range Picker Trigger */}
           <Box
             onClick={(e) => setDateRangeAnchor(e.currentTarget)}
             sx={{
               display: "flex",
               alignItems: "center",
               gap: "8px",
               padding: "4px 12px",
               borderRadius: "8px",
               backgroundColor: "#ffffff",
               border: "1px solid rgba(0, 0, 0, 0.23)",
               cursor: "pointer",
               fontFamily: "Work Sans",
               fontSize: "13px",
               color: "#14285A",
               height: "40px",
               boxSizing: "border-box",
               userSelect: "none",
               transition: "border-color 0.2s ease",
               "&:hover": {
                 borderColor: "#007c92",
               },
             }}
           >
             <DateRangeIcon sx={{ color: "#007c92", fontSize: "20px" }} />
             <Box
               component="span"
               sx={{
                 fontWeight: 500,
                 fontSize: "13px",
                 color: "#294A50",
                 whiteSpace: "nowrap",
               }}
             >
               {formatDateRangeLabel(startDate, endDate)}
             </Box>
           </Box>

           <Popover
             open={Boolean(dateRangeAnchor)}
             anchorEl={dateRangeAnchor}
             onClose={() => setDateRangeAnchor(null)}
             anchorOrigin={{
               vertical: "bottom",
               horizontal: "left",
             }}
             transformOrigin={{
               vertical: "top",
               horizontal: "left",
             }}
             PaperProps={{
               sx: {
                 p: 2,
                 mt: 1,
                 borderRadius: "16px",
                 boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.14)",
                 width: { xs: "310px", sm: "330px" },
               },
             }}
           >
             <DateRangePicker
               startDate={startDate}
               endDate={endDate}
               onApply={(start, end) => {
                 if (start && end) {
                   const s = start.isAfter(end) ? end : start;
                   const e = start.isAfter(end) ? start : end;
                   setStartDate(s);
                   setEndDate(e);
                 } else if (start) {
                   setStartDate(start);
                   setEndDate(start);
                 } else if (end) {
                   setStartDate(end);
                   setEndDate(end);
                 }
                 setDateRangeAnchor(null);
               }}
               onClear={() => {
                 setStartDate(dayjs().subtract(13, "day"));
                 setEndDate(dayjs());
                 setDateRangeAnchor(null);
               }}
               onClose={() => setDateRangeAnchor(null)}
             />
           </Popover>

           {/* Unified Filter Button */}
           <div
             onClick={(e) => setFilterAnchor(e.currentTarget)}
             style={{
               display: "flex",
               alignItems: "center",
               gap: "8px",
               padding: "4px 10px",
               borderRadius: "8px",
               backgroundColor: "#ffffff",
               border: "1px solid rgba(0, 0, 0, 0.23)",
               cursor: "pointer",
               fontFamily: "Work Sans",
               fontSize: "13px",
               color: "#14285A",
               height: "40px",
               boxSizing: "border-box",
               userSelect: "none",
             }}
           >
             <FilterAltIcon style={{ color: "#007c92", fontSize: "20px" }} />
             <span style={{ fontWeight: 500 }}>Filter</span>

             {/* Chip for Device if selected */}
             {deviceId && (
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   backgroundColor: "#e8f4f7",
                   padding: "2px 8px",
                   borderRadius: "12px",
                   fontSize: "11px",
                   fontWeight: 600,
                   color: "#007c92",
                   border: "1px solid #007c92",
                 }}
               >
                 {data?.devices.find((d) => d.device_id === deviceId)?.device_name || deviceId}
                 <IconButton
                   size="small"
                   onClick={(e) => {
                     e.stopPropagation();
                     setDeviceId("");
                   }}
                   sx={{ ml: 0.5, p: 0, color: "#007c92" }}
                 >
                   <ClearIcon sx={{ fontSize: 13 }} />
                 </IconButton>
               </Box>
             )}

             {/* Chip if AI Predictions Only */}
             {!considerOverrides && (
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   backgroundColor: "#f0f4f8",
                   padding: "2px 8px",
                   borderRadius: "12px",
                   fontSize: "11px",
                   fontWeight: 600,
                   color: "#475569",
                   border: "1px solid #94a3b8",
                 }}
               >
                 AI Only
                 <IconButton
                   size="small"
                   onClick={(e) => {
                     e.stopPropagation();
                     setConsiderOverrides(true);
                   }}
                   sx={{ ml: 0.5, p: 0, color: "#475569" }}
                 >
                   <ClearIcon sx={{ fontSize: 13 }} />
                 </IconButton>
               </Box>
             )}

             {/* Chip if override filter active */}
             {operatorStatus && (
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   backgroundColor: "#fdf3eb",
                   padding: "2px 8px",
                   borderRadius: "12px",
                   fontSize: "11px",
                   fontWeight: 600,
                   color: "#c26510",
                   border: "1px solid #c26510",
                 }}
               >
                 {operatorStatus === "all"
                   ? "Only Overridden"
                   : operatorStatus === "none"
                   ? "Non-Overridden"
                   : `Operator: ${operatorStatus}`}
                 <IconButton
                   size="small"
                   onClick={(e) => {
                     e.stopPropagation();
                     setOperatorStatus("");
                   }}
                   sx={{ ml: 0.5, p: 0, color: "#c26510" }}
                 >
                   <ClearIcon sx={{ fontSize: 13 }} />
                 </IconButton>
               </Box>
             )}
           </div>

           <Menu
             anchorEl={filterAnchor}
             open={Boolean(filterAnchor)}
             onClose={() => setFilterAnchor(null)}
             PaperProps={{
               style: { minWidth: "250px", borderRadius: "10px", padding: "4px 0" },
             }}
           >
             {/* Section 1: Evaluation Mode */}
             <MenuItem
               disabled
               style={{
                 fontSize: "11px",
                 fontWeight: 700,
                 color: "#9aa7b2",
                 fontFamily: "Work Sans",
                 letterSpacing: "0.08em",
                 textTransform: "uppercase",
                 opacity: 1,
                 paddingBottom: "2px",
               }}
             >
               Status Evaluation
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setConsiderOverrides(true);
                 setFilterAnchor(null);
               }}
               selected={considerOverrides}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
             >
               <div>
                 <div style={{ fontWeight: considerOverrides ? 600 : 400 }}>Consider Overrides (Final)</div>
                 <div style={{ fontSize: "11px", color: "#888" }}>Operator overrides replace predicted status</div>
               </div>
               {considerOverrides && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setConsiderOverrides(false);
                 setFilterAnchor(null);
               }}
               selected={!considerOverrides}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
             >
               <div>
                 <div style={{ fontWeight: !considerOverrides ? 600 : 400 }}>AI Predictions Only</div>
                 <div style={{ fontSize: "11px", color: "#888" }}>Pure AI model predictions (ignore overrides)</div>
               </div>
               {!considerOverrides && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>

             {/* Section 2: Filter Workpieces */}
             <MenuItem
               disabled
               style={{
                 fontSize: "11px",
                 fontWeight: 700,
                 color: "#9aa7b2",
                 fontFamily: "Work Sans",
                 letterSpacing: "0.08em",
                 textTransform: "uppercase",
                 opacity: 1,
                 paddingBottom: "2px",
                 borderTop: "1px solid #eee",
                 marginTop: "4px",
               }}
             >
               Filter By Overrides
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setOperatorStatus("");
                 setFilterAnchor(null);
               }}
               selected={operatorStatus === ""}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between" }}
             >
               <span>All Workpieces</span>
               {operatorStatus === "" && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setOperatorStatus(operatorStatus === "all" ? "" : "all");
                 setFilterAnchor(null);
               }}
               selected={operatorStatus === "all"}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between" }}
             >
               <span>Only Overridden Workpieces</span>
               {operatorStatus === "all" && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setOperatorStatus(operatorStatus === "none" ? "" : "none");
                 setFilterAnchor(null);
               }}
               selected={operatorStatus === "none"}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between" }}
             >
               <span>Only Non-Overridden Workpieces</span>
               {operatorStatus === "none" && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>

             {/* Section 3: Devices */}
             <MenuItem
               disabled
               style={{
                 fontSize: "11px",
                 fontWeight: 700,
                 color: "#9aa7b2",
                 fontFamily: "Work Sans",
                 letterSpacing: "0.08em",
                 textTransform: "uppercase",
                 opacity: 1,
                 paddingBottom: "2px",
                 borderTop: "1px solid #eee",
                 marginTop: "4px",
               }}
             >
               By Device
             </MenuItem>
             <MenuItem
               onClick={() => {
                 setDeviceId("");
                 setFilterAnchor(null);
               }}
               selected={deviceId === ""}
               style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between" }}
             >
               <span>All Devices</span>
               {deviceId === "" && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
             </MenuItem>
             {data?.devices.map((d) => (
               <MenuItem
                 key={d.device_id}
                 onClick={() => {
                   setDeviceId(deviceId === d.device_id ? "" : d.device_id);
                   setFilterAnchor(null);
                 }}
                 selected={deviceId === d.device_id}
                 style={{ fontFamily: "Work Sans", fontSize: "13px", paddingLeft: "16px", display: "flex", justifyContent: "space-between" }}
               >
                 <span>{d.device_name || d.device_id}</span>
                 {deviceId === d.device_id && <CheckIcon sx={{ fontSize: 16, color: "#007c92" }} />}
               </MenuItem>
             ))}
           </Menu>

           <MuiTooltip title="Refresh Data">
             <IconButton onClick={fetchDashboardData} sx={{ bgcolor: '#fff', borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.23)', p: '7px', '&:hover': { bgcolor: '#f5f5f5' } }}>
               <RefreshIcon sx={{ color: '#294A50' }} />
             </IconButton>
           </MuiTooltip>
           <MuiTooltip title="Export PDF Report">
             <IconButton onClick={handlePrint} sx={{ bgcolor: '#fff', borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.23)', p: '7px', '&:hover': { bgcolor: '#f5f5f5' } }}>
               <PictureAsPdfIcon sx={{ color: '#294A50' }} />
             </IconButton>
           </MuiTooltip>
        </div>
      </div>

      {/* KPI Row */}
      <div className="kpi-row">
        <div className="kpi-card">
          <span className="kpi-label">Total Inspected</span>
          <span className="kpi-value">{data?.kpi.total.toLocaleString()}</span>
          <span className="kpi-subtext">across {data?.date_range.days} days</span>
          <AssessmentIcon className="kpi-icon" sx={{ color: '#239BB9' }} />
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Passed</span>
          <span className="kpi-value" style={{ color: '#13A13E' }}>{data?.kpi.passed.toLocaleString()}</span>
          <span className="kpi-subtext">
            {considerOverrides ? "good workpieces (final)" : "good workpieces (AI-only)"}
          </span>
          <CheckCircleIcon className="kpi-icon" sx={{ color: '#13A13E' }} />
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Rejected</span>
          <span className="kpi-value" style={{ color: '#E25984' }}>{data?.kpi.rejected.toLocaleString()}</span>
          <span className="kpi-subtext">
            {considerOverrides ? "defective workpieces (final)" : "defective workpieces (AI-only)"}
          </span>
          <ErrorIcon className="kpi-icon" sx={{ color: '#E25984' }} />
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Pass Rate</span>
          <span className="kpi-value" style={{ color: '#294A50' }}>{data?.kpi.pass_rate}%</span>
          <span className="kpi-subtext">
            {considerOverrides ? "final yield" : "model efficiency"}
          </span>
          <RadarIcon className="kpi-icon" sx={{ color: '#294A50' }} />
        </div>
      </div>

      {/* Daily Summary Strip */}
      <div className="daily-strip-container">
        <div className="strip-title">Daily Summary</div>
        <div className="daily-strip">
          {data?.daily_summary.map(day => (
            <div 
              key={day.date} 
              className={`day-card ${selectedDay === day.date ? 'selected' : ''}`}
              onClick={() => setSelectedDay(day.date)}
            >
              <span className="day-date">{dayjs(day.date).format('MMM D')}</span>
              <span className="day-total">{day.total}</span>
              <div className="day-details">
                <span className="day-passed">✓ {day.passed}</span>
                <span className="day-rejected">△ {day.rejected}</span>
                <span className="day-rate" style={{ color: day.pass_rate > 90 ? '#13A13E' : day.pass_rate > 70 ? '#b1b000' : '#E25984' }}>
                  {day.pass_rate}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Charts Grid */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Daily Pass / Reject Count</span>
          </div>
          <div className="chart-content">
            <Bar 
              data={dailyPassRejectData} 
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } },
                scales: { x: { stacked: true }, y: { stacked: true } }
              }} 
            />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Overall Pass / Reject Ratio</span>
          </div>
          <div className="chart-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <div style={{ width: '220px', height: '220px' }}>
              <Doughnut 
                data={overallPassRejectData} 
                options={{ 
                  maintainAspectRatio: false, 
                  plugins: { legend: { display: false } } 
                }} 
              />
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}>
                <Typography variant="h4" fontWeight="800" color="#294A50">{data?.kpi.pass_rate}%</Typography>
                <Typography variant="caption" color="#666">Pass Rate</Typography>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '10px' }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#13A13E' }}></div>
                <Typography variant="caption">Pass</Typography>
             </div>
             <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#E25984' }}></div>
                <Typography variant="caption">Reject</Typography>
             </div>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Daily Pass Rate Trend</span>
          </div>
          <div className="chart-content">
            <Line 
              data={passRateTrendData} 
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { min: 0, max: 100 } }
              }} 
            />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Hourly Distribution — {dayjs(selectedDay).format('MMM D')}</span>
          </div>
          <div className="chart-content">
            <Bar 
              data={hourlyDistData} 
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } },
                scales: { x: { stacked: true }, y: { stacked: true } }
              }} 
            />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Profile-wise Volume</span>
          </div>
          <div className="chart-content scrollable-chart-container">
            <div style={{ height: Math.max(300, (data?.profile_summary.length || 0) * 40) + 'px', minWidth: '100%' }}>
              <Bar 
                data={profileVolumeData} 
                options={{
                  maintainAspectRatio: false,
                  indexAxis: 'y',
                  plugins: { legend: { position: 'bottom' } },
                  scales: { x: { stacked: true }, y: { stacked: true } }
                }} 
              />
            </div>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <span className="chart-title">Profile Reject Rate</span>
          </div>
          <div className="chart-content">
            <div className="profile-table-container">
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Total</th>
                    <th>Reject Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.profile_summary.map(p => (
                    <tr key={p.profile}>
                      <td>{p.profile}</td>
                      <td>{p.total}</td>
                      <td>
                        <span className="reject-rate-badge" style={{ 
                          backgroundColor: p.reject_rate > 20 ? '#FEE0E5' : '#EEFBF2',
                          color: p.reject_rate > 20 ? '#E25984' : '#13A13E'
                        }}>
                          {p.reject_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Daily Trend */}
      <div className="chart-card" style={{ minHeight: '450px' }}>
          <div className="chart-header">
            <span className="chart-title">Profile-wise Daily Trend (Total Inspected)</span>
          </div>
          <div className="chart-content">
            <Line 
              data={profileTrendData} 
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, usePointStyle: true } } },
                scales: { y: { beginAtZero: true } }
              }} 
            />
          </div>
      </div>

    </div>
  );
};

export default HomePage;
